// src/components/BrandIdentity.js
import React, { useState } from 'react';

const BrandIdentity = () => {
  const [formData, setFormData] = useState({
    logo: null,
    primaryColor: '#000000',
    secondaryColor: '#ffffff',
    accentColor: '#ff0000',
    typography: 'sans-serif',
    universalIcons: '',
    componentDesign: '',
  });

  const fonts = [
    { value: 'sans-serif', label: 'Sans Serif' },
    { value: 'serif', label: 'Serif' },
    { value: 'monospace', label: 'Monospace' },
    // Add more font options as needed.
  ];

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'file' ? files[0] : value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle submission logic: save data to your backend, etc.
    console.log('Brand Identity Data:', formData);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gray-800 text-white rounded-md shadow-md">
      <h1 className="text-3xl font-bold mb-4">Brand Identity</h1>
      <p className="mb-6">Configure your visual brand identity below:</p>
      <div className="flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-6">
        <form onSubmit={handleSubmit} className="space-y-6 flex-1">
          {/* Logo */}
          <div>
            <label htmlFor="logo" className="block text-sm font-medium text-gray-300">
              Logo
            </label>
            <input
              type="file"
              name="logo"
              id="logo"
              onChange={handleChange}
              className="mt-1 block w-full text-gray-900 bg-gray-100 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>
          {/* Color Selectors in Grid */}
          <div className="grid grid-cols-3 gap-4">
            {/* Primary Color */}
            <div>
              <label htmlFor="primaryColor" className="block text-sm font-medium text-gray-300">
                Primary Color
              </label>
              <input
                type="color"
                name="primaryColor"
                id="primaryColor"
                value={formData.primaryColor}
                onChange={handleChange}
                className="mt-1 h-10 w-16 p-0 border-0"
              />
            </div>
            {/* Secondary Color */}
            <div>
              <label htmlFor="secondaryColor" className="block text-sm font-medium text-gray-300">
                Secondary Color
              </label>
              <input
                type="color"
                name="secondaryColor"
                id="secondaryColor"
                value={formData.secondaryColor}
                onChange={handleChange}
                className="mt-1 h-10 w-16 p-0 border-0"
              />
            </div>
            {/* Accent Color */}
            <div>
              <label htmlFor="accentColor" className="block text-sm font-medium text-gray-300">
                Accent Color
              </label>
              <input
                type="color"
                name="accentColor"
                id="accentColor"
                value={formData.accentColor}
                onChange={handleChange}
                className="mt-1 h-10 w-16 p-0 border-0"
              />
            </div>
          </div>
          {/* Typography */}
          <div>
            <label htmlFor="typography" className="block text-sm font-medium text-gray-300">
              Typography
            </label>
            <select
              name="typography"
              id="typography"
              value={formData.typography}
              onChange={handleChange}
              className="mt-1 block w-full p-2 rounded-md border border-gray-300 bg-gray-100 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              {fonts.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.label}
                </option>
              ))}
            </select>
          </div>
          {/* Universal Icons */}
          <div>
            <label htmlFor="universalIcons" className="block text-sm font-medium text-gray-300">
              Universal Icons
            </label>
            <input
              type="text"
              name="universalIcons"
              id="universalIcons"
              value={formData.universalIcons}
              onChange={handleChange}
              placeholder="Enter icons or icon library names"
              className="mt-1 block w-full p-2 rounded-md border border-gray-300 bg-gray-100 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>
          {/* Universal Component Design */}
          <div>
            <label htmlFor="componentDesign" className="block text-sm font-medium text-gray-300">
              Universal Component Design (Buttons, forms, cards)
            </label>
            <textarea
              name="componentDesign"
              id="componentDesign"
              value={formData.componentDesign}
              onChange={handleChange}
              placeholder="Describe component design details"
              rows="4"
              className="mt-1 block w-full p-2 rounded-md border border-gray-300 bg-gray-100 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>
          <button
            type="submit"
            className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-500 rounded-md font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-600"
          >
            Save Brand Identity
          </button>
        </form>

        {/* Preview Section */}
        <div className="flex-1 mt-8 md:mt-0 p-4 bg-gray-700 rounded-md">
          <h2 className="text-xl font-semibold mb-2">Preview</h2>
          <div
            className="p-4 rounded-md"
            style={{
              backgroundColor: formData.secondaryColor,
              color: formData.primaryColor,
              fontFamily: formData.typography,
            }}
          >
            <p className="mb-2">This is a preview of your brand identity.</p>
            <div className="flex items-center space-x-4 mb-2">
              <div className="flex flex-col items-center">
                <span className="block text-sm font-medium">Primary</span>
                <div
                  className="w-10 h-10 rounded-full border"
                  style={{ backgroundColor: formData.primaryColor }}
                ></div>
              </div>
              <div className="flex flex-col items-center">
                <span className="block text-sm font-medium">Secondary</span>
                <div
                  className="w-10 h-10 rounded-full border"
                  style={{ backgroundColor: formData.secondaryColor }}
                ></div>
              </div>
              <div className="flex flex-col items-center">
                <span className="block text-sm font-medium">Accent</span>
                <div
                  className="w-10 h-10 rounded-full border"
                  style={{ backgroundColor: formData.accentColor }}
                ></div>
              </div>
            </div>
            <p className="mb-2">
              <strong>Typography:</strong> {formData.typography}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandIdentity;
